import { createVerifier, challengeFor, randomUrlSafe } from './pkce';
import {
  buildAuthorizeUrl,
  buildLogoutUrl,
  exchangeCode,
  refreshTokens,
  revokeToken,
  type RuntimeConfig,
  type TokenSet,
} from './oauth';

/**
 * Token storage and the sign-in/sign-out lifecycle.
 *
 * Tokens live in module memory and are never persisted. A hard refresh therefore loses the session
 * and redirects to /oauth2/authorize - but Cognito's own session cookie makes that a no-interaction
 * round trip, so the user sees a flash rather than a login prompt. The trade buys a real property:
 * the refresh token, which is valid for the pool's default 30 days, never touches disk where an XSS
 * could read it.
 */

const VERIFIER_KEY = 'replit-audit.pkce.verifier';
const STATE_KEY = 'replit-audit.pkce.state';
const RETURN_TO_KEY = 'replit-audit.returnTo';

let tokens: TokenSet | undefined;
let refreshTimer: ReturnType<typeof setTimeout> | undefined;
let onExpired: (() => void) | undefined;

export function setTokens(next: TokenSet | undefined, config?: RuntimeConfig): void {
  tokens = next;
  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = undefined;
  if (next && config) scheduleRefresh(config);
}

export function getTokens(): TokenSet | undefined {
  return tokens;
}

export function onSessionExpired(callback: () => void): void {
  onExpired = callback;
}

function scheduleRefresh(config: RuntimeConfig): void {
  if (!tokens?.refreshToken) return;
  // Two minutes of headroom, and never less than five seconds out even for a near-expired token.
  const delay = Math.max(tokens.expiresAt - Date.now() - 120_000, 5_000);
  refreshTimer = setTimeout(() => {
    void refreshNow(config).catch(() => onExpired?.());
  }, delay);
}

export async function refreshNow(config: RuntimeConfig): Promise<TokenSet> {
  if (!tokens?.refreshToken) throw new Error('No refresh token');
  const next = await refreshTokens(config, tokens.refreshToken);
  setTokens(next, config);
  return next;
}

/**
 * Starts the sign-in redirect.
 *
 * sessionStorage rather than localStorage for the verifier and state: it is scoped to this tab and
 * dies with it, so a verifier cannot outlive the flow it belongs to or leak into a sibling tab. An
 * XSS on this origin could read it - but the same XSS could read the tokens and just call the API,
 * so the verifier is not the marginal risk. In-memory is impossible here: /oauth2/authorize is a
 * full-page navigation, so any memory is gone before the code comes back.
 */
export async function beginSignIn(config: RuntimeConfig, returnTo?: string): Promise<void> {
  const verifier = createVerifier();
  const state = randomUrlSafe(16);

  sessionStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  if (returnTo) sessionStorage.setItem(RETURN_TO_KEY, returnTo);

  window.location.assign(buildAuthorizeUrl(config, { state, challenge: await challengeFor(verifier) }));
}

/** Completes the flow at /auth/callback. Returns the path the user was originally heading to. */
export async function completeSignIn(config: RuntimeConfig, search: string): Promise<string> {
  const params = new URLSearchParams(search);
  const verifier = sessionStorage.getItem(VERIFIER_KEY);
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY) ?? '/';

  try {
    const error = params.get('error');
    if (error) {
      // Cognito surfaces the trigger's refusal here. Its message is deliberately opaque; the real
      // reason is in the pre-token-generation log group.
      throw new Error(params.get('error_description') || error);
    }

    const code = params.get('code');
    if (!code) throw new Error('No authorization code in the callback URL.');
    if (!verifier) throw new Error('Sign-in did not start in this tab. Try again.');
    if (!expectedState || params.get('state') !== expectedState) {
      throw new Error('State mismatch - the sign-in response did not match this tab’s request.');
    }

    setTokens(await exchangeCode(config, { code, verifier }), config);
    return returnTo;
  } finally {
    // Always, even on failure: a verifier that survives a failed exchange is reusable.
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(STATE_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
  }
}

/**
 * Sign-out.
 *
 * What this clears, and what it does not:
 *   - in-memory tokens                                          cleared
 *   - the refresh token's server-side validity                  cleared, if revoke succeeded
 *     (which also invalidates access tokens minted from it)
 *   - the already-issued ID token                               NOT cleared; valid until exp
 *   - Cognito's hosted-UI session cookie on the broker domain   cleared, only via /logout below
 *   - the Microsoft session on login.microsoftonline.com        NOT cleared
 *
 * Cognito's session cookie can only be cleared by a full-page navigation to its /logout endpoint,
 * and that endpoint honours a logout_uri only if it is registered in the app client's LogoutURLs.
 * So this returns 'redirecting' when this site has one registered - the caller must not navigate
 * afterwards, since the browser is already leaving - and 'local' when it does not, in which case the
 * Cognito cookie survives and the signed-out screen has to say so.
 *
 * Revocation happens before the redirect either way, and is awaited: once the navigation starts, a
 * pending fetch on this page is not guaranteed to finish.
 *
 * The Microsoft session survives in both cases. Only a tenant-level Entra logout ends that one, and
 * the signed-out screen links to it.
 */
export async function signOut(config: RuntimeConfig): Promise<'redirecting' | 'local'> {
  const refreshToken = tokens?.refreshToken;
  setTokens(undefined);
  if (refreshToken) await revokeToken(config, refreshToken);

  const logoutUrl = buildLogoutUrl(config);
  if (!logoutUrl) return 'local';

  // Cognito clears its cookie and returns the browser to the registered logout URI, which for this
  // site is its /signed-out route - reachable without a session by design.
  window.location.assign(logoutUrl);
  return 'redirecting';
}

/** The tenant-scoped Microsoft sign-out link offered on the signed-out screen. */
export function microsoftLogoutUrl(config: RuntimeConfig): string {
  return `https://login.microsoftonline.com/${config.entraTenantId}/oauth2/v2.0/logout`;
}
